import Button from '@atlaskit/button';
import { LinkButton } from '@atlaskit/button/new';

import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Action, redirect } from 'redux-first-router';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'model/State';
import { extractTLDFrom } from 'modules/siteconfigurator/utils';
import { Form, ErrorMessage } from 'modules/stripe-credit-card';
import { RESTRICTED_COUNTRIES_ISO_CODES, RESTRICTED_EMAIL_DOMAINS } from 'modules/utils';
import config from 'appconfig';
import t from 'modules/i18n/intl';
import { useStripeOnSubmitMAC } from 'util/mac/stripeUtil';

import MACPageHeader from './components/macPageHeader';
import { PaymentMethodsTable } from './components/PaymentMethodsTable';
import {
  BillingStatementNote,
  CCFormWrapper,
  HeaderContainer,
  PageH3Header,
  PageTitle,
} from './components/styledComponents';
import { fetchRenewalTotal } from './duck/actions/accountId';
import { fetchCountryByName } from './duck/actions/country';
import { postUpdateCCDetails } from './duck/actions/creditCard';
import { route } from './duck/actions/landing';
import {
  getCurrentMACContactEmail,
  getCurrentMACContactOrgCountry,
  selectCountriesByName,
  selectCreditCardUpdatableAccounts,
} from './duck/selectors';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';
import { CloudProductKeys } from 'data/products/keys';

const atlassianCustomerAgreement = (chunks) => (
  <a
    data-link="customer-agreement"
    href={config.legalCustomerAgreement}
    target="_blank"
    rel="noreferrer"
    title="Atlassian Customer Agreement"
  >
    {chunks}
  </a>
);

const atlassianPricing = (chunks) => (
  <a
    href="https://www.atlassian.com/licensing/purchase-licensing#pricinganddiscounts-1"
    target="_blank"
    rel="noreferrer"
    title="Atlassian Pricing"
  >
    {chunks}
  </a>
);

const UpdateCreditCardPage = () => {
  const MACCloudReadModeFG = checkFeatureGate(FeatureFlags.CART_MAC_CLOUD_READ_MODE);

  const dispatch = useDispatch();

  const accountId = useSelector((state: State) => state.location.payload.accountId);
  const currentContactEmail = useSelector(getCurrentMACContactEmail) || '';
  const currentContactOrgCountry = useSelector(getCurrentMACContactOrgCountry);
  const creditCardUpdatableAccounts: any = useSelector(selectCreditCardUpdatableAccounts);
  const orgCountriesFromAccounts = useSelector(selectCountriesByName) || {};

  const [accountsSelected, setAccountsSelected] = useState<number[]>([]);
  const [isRuBy, setIsRuBy] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(0);
  const [finalListOfAccounts, setFinalListOfAccounts] = useState<any>();

  useEffect(() => {
    document.title = 'My Atlassian | Update Saved Credit Card';
  }, [
    accountId,
    currentContactEmail,
    currentContactOrgCountry,
    creditCardUpdatableAccounts,
    orgCountriesFromAccounts,
  ]);

  useEffect(() => {
    if (creditCardUpdatableAccounts?.length) {
      setFinalListOfAccounts(
        MACCloudReadModeFG
          ? creditCardUpdatableAccounts.filter((account) => {
              const productPackage = account.merchandise?.productPackage || account.productPackage;
              return (
                productPackage?.hostingType !== 'HOSTED' ||
                productPackage.key === CloudProductKeys.BITBUCKET
              );
            })
          : creditCardUpdatableAccounts,
      );
    }
  }, [creditCardUpdatableAccounts?.length, MACCloudReadModeFG]);

  useEffect(() => {
    if (
      finalListOfAccounts?.length &&
      accountId &&
      finalListOfAccounts.find(({ id }) => id === +accountId)
    ) {
      setAccountsSelected([+accountId]);
    }
  }, [finalListOfAccounts?.length]);

  useEffect(() => {
    if (currentContactOrgCountry) {
      dispatch(fetchCountryByName(currentContactOrgCountry));
    }
  }, [currentContactOrgCountry]);

  useEffect(() => {
    if (finalListOfAccounts?.length && accountsSelected) {
      const mainContactOrgCountryOfFirstAccount = finalListOfAccounts[0].contactRelationships?.find(
        ({ main }) => main,
      )?.contact.organisationDetails.country;
      const mainContactOrgCountryOfCurrentAccount = finalListOfAccounts
        .find(({ id }) => id === accountsSelected)
        ?.contactRelationships.find(({ main }) => main)?.contact.organisationDetails.country;

      if (mainContactOrgCountryOfCurrentAccount) {
        dispatch(fetchCountryByName(mainContactOrgCountryOfCurrentAccount));
      }
      if (
        mainContactOrgCountryOfFirstAccount &&
        mainContactOrgCountryOfFirstAccount !== mainContactOrgCountryOfCurrentAccount
      ) {
        dispatch(fetchCountryByName(mainContactOrgCountryOfFirstAccount));
      }
    }
  }, [finalListOfAccounts?.length, accountsSelected]);

  useEffect(() => {
    finalListOfAccounts?.forEach((account) => {
      dispatch(fetchRenewalTotal(account?.id, false, false, false, false));
    });
  }, [finalListOfAccounts]);

  useEffect(() => {
    const countryISOCodesFromAccounts = Object.values(orgCountriesFromAccounts).map(
      ({ _result }) => _result?.countryIsoCode,
    );

    const isISORestricted = countryISOCodesFromAccounts.some((item) =>
      RESTRICTED_COUNTRIES_ISO_CODES.includes(item),
    );

    const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(
      extractTLDFrom(currentContactEmail),
    );

    if (isISORestricted || isEmailRestricted) {
      setIsRuBy(true);
    } else {
      setIsRuBy(false);
    }
  }, [currentContactEmail, orgCountriesFromAccounts]);

  const handleBillingDetailsFormSubmit = useStripeOnSubmitMAC(() => {
    if (isRuBy) return;
    accountsSelected.forEach((id) => {
      dispatch(postUpdateCCDetails(id));
    });

    dispatch(redirect(route() as Action));
  });

  return (
    <>
      <MACPageHeader />
      <HeaderContainer>
        <PageTitle>
          <FormattedMessage
            defaultMessage="Update Saved Credit Card"
            id="mac.update-cc-page.header"
          />
        </PageTitle>
      </HeaderContainer>
      <p>
        <FormattedMessage
          defaultMessage="Please select the products you wish to update the credit card details of:"
          id="mac.update-cc-page.content.p1"
        />
      </p>
      <PaymentMethodsTable
        accountsSelected={accountsSelected}
        setAccountsSelected={setAccountsSelected}
        paymentData={finalListOfAccounts}
      />
      <PageH3Header>
        <FormattedMessage
          defaultMessage="New Credit Card Details"
          id="mac.update-cc-page.header-2"
        />
      </PageH3Header>
      <p>
        <FormattedMessage
          defaultMessage="Future renewals of the selected products above will use the credit card details provided below."
          id="mac.update-cc-page.content.p2"
        />
      </p>
      <form
        onSubmit={(event) => {
          setIsSubmitted(isSubmitted + 1);
          handleBillingDetailsFormSubmit(event);
        }}
      >
        <CCFormWrapper>
          <Form autoFocus frameEmbeddingMitigation={false} />
          <ErrorMessage />
        </CCFormWrapper>
        <p>
          {t(
            {
              defaultMessage:
                'By clicking here you accept the <atlassianCustomerAgreement>Atlassian Customer Agreement</atlassianCustomerAgreement> and agree to pay fees charged in accordance with our <atlassianPricing>Atlassian pricing</atlassianPricing>.',
              id: 'mac.update-cc-page.agreement',
            },
            {
              atlassianCustomerAgreement,
              atlassianPricing,
            },
          )}
        </p>
        <br />
        <Button
          data-cy="update-cc-submit-button"
          appearance="primary"
          type="submit"
          isDisabled={!accountsSelected.length || isRuBy || !!isSubmitted}
        >
          <FormattedMessage defaultMessage="Save" id="mac.update-cc-page.button.save" />
        </Button>{' '}
        <LinkButton data-cy="update-cc-cancel-button" href="/purchase/my">
          <FormattedMessage defaultMessage="Cancel" id="mac.update-cc-page.button.cancel" />
        </LinkButton>
      </form>
      <BillingStatementNote>
        <FormattedMessage
          defaultMessage="Your billing statement will display Atlassian B.V., located in Amsterdam, Netherlands. Atlassian Pty Limited, our principal place of business, is at Level 6, 341 George Street, Sydney NSW Australia 2000. Your credit card issuer may charge foreign transaction or cross-border fees in addition to the total price above."
          id="mac.update-cc-page.billing-statement-note"
        />
      </BillingStatementNote>
    </>
  );
};

export default UpdateCreditCardPage;
